import { AbdmFhirBundleIndex } from './abdm-fhir-bundle-index';
import {
  FhirObject, asObject, joinDetails, readConcept, readConcepts, readDate, readNotes, readObjects,
  readQuantity, readReference, readReferenceDisplay, readString
} from './abdm-fhir-readers';

export interface AbdmResourceDescription {
  readonly label: string;
  readonly value: string;
}

export type AbdmResourceDescriber = (resource: FhirObject, index: AbdmFhirBundleIndex) => AbdmResourceDescription;

export interface AbdmResourceGroup {
  readonly title: string;
  readonly types: readonly string[];
  readonly describe: AbdmResourceDescriber;
}

// One group per kind of clinical content, in the order a clinician reads a record.
export const ABDM_RESOURCE_GROUPS: readonly AbdmResourceGroup[] = [
  { title: 'Conditions', types: ['Condition'], describe: describeCondition },
  { title: 'Allergies', types: ['AllergyIntolerance'], describe: describeAllergy },
  { title: 'Observations', types: ['Observation'], describe: describeObservation },
  { title: 'Medications', types: ['MedicationRequest', 'MedicationStatement'], describe: describeMedication },
  { title: 'Procedures', types: ['Procedure'], describe: describeProcedure },
  { title: 'Reports', types: ['DiagnosticReport'], describe: describeDiagnosticReport },
  { title: 'Investigations advised', types: ['ServiceRequest'], describe: describeServiceRequest },
  { title: 'Immunizations', types: ['Immunization'], describe: describeImmunization },
  { title: 'Family history', types: ['FamilyMemberHistory'], describe: describeFamilyHistory },
  { title: 'Care plan', types: ['CarePlan'], describe: describeCarePlan },
  { title: 'Follow up', types: ['Appointment'], describe: describeAppointment },
  { title: 'Documents', types: ['DocumentReference'], describe: describeDocument }
];

const SCALAR_VALUE_PROPERTIES: readonly string[] = ['valueString', 'valueInteger', 'valueBoolean', 'valueDateTime'];

function describeCondition(resource: FhirObject): AbdmResourceDescription {
  const details = [readConcept(resource, 'clinicalStatus'), readConcept(resource, 'severity'),
    readDate(readString(resource, 'onsetDateTime')), readNotes(resource)];
  return { label: readConcept(resource, 'code') || 'Condition', value: joinDetails(details) || '' };
}

function describeAllergy(resource: FhirObject): AbdmResourceDescription {
  const reactions = readObjects(resource, 'reaction').flatMap(reaction => readConcepts(reaction, 'manifestation'));
  const details = [readConcept(resource, 'clinicalStatus'), ...reactions, readNotes(resource)];
  return { label: readConcept(resource, 'code') || 'Allergy', value: joinDetails(details) || '' };
}

function describeObservation(resource: FhirObject): AbdmResourceDescription {
  const components = readObjects(resource, 'component')
    .map(component => joinLabelled(readConcept(component, 'code'), readObservationValue(component)));
  const details = [readObservationValue(resource), ...components,
    readConcepts(resource, 'interpretation')[0], readNotes(resource)];
  return { label: readConcept(resource, 'code') || 'Observation', value: joinDetails(details) || '' };
}

function readObservationValue(resource: FhirObject): string | undefined {
  const scalar = SCALAR_VALUE_PROPERTIES.map(property => resource[property])
    .find(value => value !== undefined && value !== null);
  if (scalar !== undefined) {
    return String(scalar);
  }
  return readQuantity(asObject(resource['valueQuantity'])) || readConcept(resource, 'valueCodeableConcept');
}

function joinLabelled(label: string | undefined, value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  return label ? `${label}: ${value}` : value;
}

function describeMedication(resource: FhirObject, index: AbdmFhirBundleIndex): AbdmResourceDescription {
  const dosages = readObjects(resource, 'dosageInstruction').concat(readObjects(resource, 'dosage'));
  return {
    label: readMedicationName(resource, index) || 'Medication',
    value: joinDetails(dosages.map(readDosage)) || 'No dosage instructions provided'
  };
}

// The medicine is either named inline or is a separate Medication resource in the bundle.
function readMedicationName(resource: FhirObject, index: AbdmFhirBundleIndex): string | undefined {
  const referenced = index.resolve(readReference(resource, 'medicationReference'));
  return readConcept(resource, 'medicationCodeableConcept')
    || readConcept(referenced, 'code')
    || readReferenceDisplay(resource, 'medicationReference');
}

function readDosage(dosage: FhirObject): string | undefined {
  const details = [readString(dosage, 'text'), ...readConcepts(dosage, 'additionalInstruction'),
    readConcept(dosage, 'route')];
  return joinDetails(details);
}

function describeProcedure(resource: FhirObject): AbdmResourceDescription {
  const details = [readDate(readString(resource, 'performedDateTime')), readString(resource, 'status'),
    readConcept(resource, 'outcome'), readNotes(resource)];
  return { label: readConcept(resource, 'code') || 'Procedure', value: joinDetails(details) || '' };
}

function describeDiagnosticReport(resource: FhirObject): AbdmResourceDescription {
  const details = [readString(resource, 'conclusion'), readDate(readString(resource, 'effectiveDateTime')),
    readString(resource, 'status')];
  return { label: readConcept(resource, 'code') || 'Report', value: joinDetails(details) || '' };
}

function describeServiceRequest(resource: FhirObject): AbdmResourceDescription {
  const details = [readString(resource, 'status'), readNotes(resource)];
  return { label: readConcept(resource, 'code') || 'Investigation', value: joinDetails(details) || '' };
}

function describeImmunization(resource: FhirObject): AbdmResourceDescription {
  const details = [readDate(readString(resource, 'occurrenceDateTime')), readString(resource, 'lotNumber'),
    readNotes(resource)];
  return { label: readConcept(resource, 'vaccineCode') || 'Vaccine', value: joinDetails(details) || '' };
}

function describeFamilyHistory(resource: FhirObject): AbdmResourceDescription {
  const conditions = readObjects(resource, 'condition').map(condition => readConcept(condition, 'code'));
  return { label: readConcept(resource, 'relationship') || 'Family member', value: joinDetails(conditions) || '' };
}

function describeCarePlan(resource: FhirObject): AbdmResourceDescription {
  const details = [readString(resource, 'description'), readNotes(resource)];
  return { label: readString(resource, 'title') || 'Care plan', value: joinDetails(details) || '' };
}

function describeAppointment(resource: FhirObject): AbdmResourceDescription {
  const details = [readDate(readString(resource, 'start')), readString(resource, 'description')];
  return { label: readConcepts(resource, 'serviceType')[0] || 'Follow up', value: joinDetails(details) || '' };
}

function describeDocument(resource: FhirObject): AbdmResourceDescription {
  const titles = readObjects(resource, 'content')
    .map(content => readString(asObject(content['attachment']), 'title'));
  const details = [readString(resource, 'description'), ...titles];
  return { label: readConcept(resource, 'type') || 'Document', value: joinDetails(details) || '' };
}
